/** Gift cards: list (status filter, search), issue, detail drawer with ledger, adjust balance, disable/enable, resend. */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Descriptions, Drawer, Empty, Form, Input, InputNumber, Modal, Result, Space, Switch, Table, Tabs, Tag, Timeline, Typography, message } from 'antd';
import { GiftOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { CopyLine, fmtDate, listOf, money, toMinor, toastErr, totalOf, useSite } from './common';
import { MoneyInput } from './ShopCommon';

const { Text } = Typography;

const IssueModal = ({ open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api, currency } = useSite();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState(null);
    useEffect(() => { if (open) { form.resetFields(); setIssued(null); } }, [open, form]);
    const submit = async () => {
        const v = await form.validateFields();
        setBusy(true);
        try {
            const r = await api.post('/gift-cards', {
                value: v.value, code: v.code || undefined, recipient_email: v.recipient_email || undefined, recipient_name: v.recipient_name || undefined,
                message: v.message || undefined, note: v.note || undefined, expires_at: v.expires_at ? v.expires_at.format('YYYY-MM-DD') : undefined, send: !!v.send,
            });
            setIssued(r);
            onDone();
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} title={t('Issue gift card')} onCancel={onClose} destroyOnHidden
            footer={issued ? <Button type="primary" onClick={onClose}>{t('Done')}</Button> : undefined} onOk={submit} okText={t('Issue', { context: 'shop' })} confirmLoading={busy}>
            {issued ? (
                <Result status="success" icon={<GiftOutlined />} title={money(issued.initial_value ?? issued.balance, issued.currency)}
                    subTitle={issued.code ? <CopyLine value={issued.code} /> : issued.masked_code} extra={issued.code ? <Text type="secondary">{t('The full code is shown only once.')}</Text> : null} />
            ) : (
                <Form form={form} layout="vertical" requiredMark={false}>
                    <Form.Item name="value" label={t('Amount')} rules={[{ required: true, message: t('Enter a value') }]}><MoneyInput currency={currency} style={{ width: 200 }} /></Form.Item>
                    <Space wrap>
                        <Form.Item name="recipient_email" label={t('Recipient email')} rules={[{ type: 'email' }]}><Input style={{ width: 240 }} /></Form.Item>
                        <Form.Item name="recipient_name" label={t('Recipient name')}><Input style={{ width: 200 }} /></Form.Item>
                    </Space>
                    <Form.Item name="send" valuePropName="checked" label={t('Email it now')}><Switch /></Form.Item>
                    <Form.Item name="message" label={t('Message')}><Input.TextArea autoSize={{ minRows: 2 }} maxLength={1000} /></Form.Item>
                    <Space wrap>
                        <Form.Item name="code" label={t('Custom code')} tooltip={t('Leave empty to generate one')}><Input style={{ width: 220 }} /></Form.Item>
                        <Form.Item name="expires_at" label={t('Expires')}><DatePicker /></Form.Item>
                    </Space>
                    <Form.Item name="note" label={t('Internal note')}><Input /></Form.Item>
                </Form>
            )}
        </Modal>
    );
};

const Detail = ({ id, onClose, onChanged }) => {
    const { t } = useTranslation();
    const { api, currency } = useSite();
    const [g, setG] = useState(null);
    const [adj, setAdj] = useState(null);
    const [note, setNote] = useState('');
    const load = useCallback(() => { if (id) api.get(`/gift-cards/${id}`).then(setG).catch(toastErr); }, [api, id]);
    useEffect(() => { setG(null); load(); }, [load]);
    const act = async (fn, ok) => { try { await fn(); if (ok) message.success(ok); load(); onChanged(); } catch (e) { toastErr(e); } };
    const cur = g?.currency || currency;
    return (
        <Drawer open={!!id} onClose={onClose} width={Math.min(520, window.innerWidth)} title={g ? g.masked_code : ''}
            extra={g && (
                <Space>
                    <Button onClick={() => act(() => api.post(`/gift-cards/${g.id}/send`, {}), t('Sent', { context: 'shop' }))} disabled={!g.recipient_email}>{t('Resend')}</Button>
                    <Button danger={g.status === 'active'} onClick={() => act(() => api.put(`/gift-cards/${g.id}`, { status: g.status === 'active' ? 'disabled' : 'active' }))}>
                        {g.status === 'active' ? t('Disable') : t('Enable')}
                    </Button>
                </Space>
            )}>
            {!g ? null : (
                <>
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label={t('Balance')}><Text strong>{money(g.balance, cur)}</Text> <Text type="secondary">/ {money(g.initial_value, cur)}</Text></Descriptions.Item>
                        <Descriptions.Item label={t('Status')}>{g.status === 'active' ? <Tag color={g.expired ? 'default' : 'green'}>{g.expired ? t('Expired') : t('Active')}</Tag> : <Tag>{t('Disabled')}</Tag>}</Descriptions.Item>
                        <Descriptions.Item label={t('Expires')}>{g.expires_at ? fmtDate(g.expires_at, false) : t('Never')}</Descriptions.Item>
                        <Descriptions.Item label={t('Recipient')}>{[g.recipient_name, g.recipient_email].filter(Boolean).join(' · ') || '-'}</Descriptions.Item>
                        {g.order_id && <Descriptions.Item label={t('Order', { context: 'shop' })}>#{g.order_id}</Descriptions.Item>}
                        {g.note && <Descriptions.Item label={t('Note', { context: 'shop' })}>{g.note}</Descriptions.Item>}
                    </Descriptions>
                    <Space.Compact style={{ marginTop: 16, width: '100%' }}>
                        <InputNumber placeholder={t('+/- amount')} value={adj} onChange={setAdj} step={1} style={{ width: 160 }} />
                        <Input placeholder={t('Reason')} value={note} onChange={(e) => setNote(e.target.value)} />
                        <Button disabled={!adj} onClick={() => act(() => api.post(`/gift-cards/${g.id}/adjust`, { amount: toMinor(adj, cur), note }), t('Saved')).then(() => { setAdj(null); setNote(''); })}>{t('Adjust')}</Button>
                    </Space.Compact>
                    <Timeline style={{ marginTop: 20 }} items={listOf(g.ledger).map((l) => ({
                        color: l.delta >= 0 ? 'green' : 'blue',
                        children: <div><Text strong>{l.delta >= 0 ? '+' : ''}{money(l.delta, cur)}</Text> <Text type="secondary">→ {money(l.balance_after, cur)}</Text><div style={{ fontSize: 12 }}><Text type="secondary">{t(l.kind)}{l.order_id ? ` · #${l.order_id}` : ''}{l.note ? ` · ${l.note}` : ''} · {fmtDate(l.created_at)}</Text></div></div>,
                    }))} />
                </>
            )}
        </Drawer>
    );
};

const ShopGiftCards = () => {
    const { t } = useTranslation();
    const { api, currency } = useSite();
    const [f, setF] = useState({ status: '', q: '', page: 1 });
    const [data, setData] = useState(null);
    const [soon, setSoon] = useState(false);
    const [issue, setIssue] = useState(false);
    const [open, setOpen] = useState(null);
    const [prodOpen, setProdOpen] = useState(false);
    const [den, setDen] = useState('25, 50, 100');

    const load = useCallback(async () => {
        try {
            const d = await api.get('/gift-cards', { status: f.status || undefined, q: f.q || undefined, page: f.page, per_page: 25 }, QUIET);
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) { if (e?.status === 404) setSoon(true); else toastErr(e); setData({ items: [], total: 0 }); }
    }, [api, f]);
    useEffect(() => { load(); }, [load]);

    const makeProduct = async () => {
        const denominations = den.split(/[,\s]+/).map(Number).filter((x) => x > 0).map((x) => toMinor(x, currency));
        try { await api.post('/gift-cards/product', { denominations }); message.success(t('Gift card product created')); setProdOpen(false); } catch (e) { toastErr(e); }
    };

    if (soon) return <Card><Result status="info" title={t('Gift cards are coming soon')} /></Card>;
    return (
        <Card size="small" title={t('Gift cards')} extra={<Space><Button onClick={() => setProdOpen(true)}>{t('Sell gift cards')}</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => setIssue(true)}>{t('Issue gift card')}</Button></Space>}>
            <Tabs activeKey={f.status} onChange={(status) => setF({ ...f, status, page: 1 })} items={[
                { key: '', label: t('All') }, { key: 'active', label: t('Active') }, { key: 'used', label: t('Used up') }, { key: 'expired', label: t('Expired') }, { key: 'disabled', label: t('Disabled') },
            ]} />
            <Input.Search allowClear placeholder={t('Last 4, full code or email')} style={{ maxWidth: 320, marginBottom: 12 }} onSearch={(q) => setF({ ...f, q, page: 1 })} />
            <Table rowKey="id" size="small" loading={!data} dataSource={data?.items} scroll={{ x: 700 }}
                onRow={(g) => ({ onClick: () => setOpen(g.id), style: { cursor: 'pointer' } })}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No gift cards yet')} /> }}
                pagination={{ current: f.page, pageSize: 25, total: data?.total, hideOnSinglePage: true, onChange: (page) => setF({ ...f, page }) }}
                columns={[
                    { title: t('Code'), dataIndex: 'masked_code', render: (v) => <Text code>{v}</Text> },
                    { title: t('Balance'), key: 'b', align: 'right', render: (_, g) => <>{money(g.balance, g.currency)} <Text type="secondary" style={{ fontSize: 12 }}>/ {money(g.initial_value, g.currency)}</Text></> },
                    { title: t('Status'), key: 's', width: 110, render: (_, g) => (g.status !== 'active' ? <Tag>{t('Disabled')}</Tag> : g.expired ? <Tag>{t('Expired')}</Tag> : g.balance === 0 ? <Tag>{t('Used up')}</Tag> : <Tag color="green">{t('Active')}</Tag>) },
                    { title: t('Recipient'), key: 'r', render: (_, g) => g.recipient_email || '-' },
                    { title: t('Source'), dataIndex: 'source', width: 100, render: (v) => t(v === 'order' ? 'Purchased' : 'Issued') },
                    { title: t('Created'), dataIndex: 'created_at', width: 120, render: (v) => fmtDate(v, false) },
                ]} />
            <IssueModal open={issue} onClose={() => setIssue(false)} onDone={load} />
            <Detail id={open} onClose={() => setOpen(null)} onChanged={load} />
            <Modal open={prodOpen} title={t('Sell gift cards')} onCancel={() => setProdOpen(false)} onOk={makeProduct} okText={t('Create product')}>
                <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('Creates a gift card product; buyers get a code by email after payment.')} />
                <Text>{t('Values')} ({currency})</Text>
                <Input value={den} onChange={(e) => setDen(e.target.value)} placeholder="25, 50, 100" style={{ marginTop: 6 }} />
            </Modal>
        </Card>
    );
};

export default ShopGiftCards;
